import {keys, zodEnum} from './util';
import {zodTypeguard} from './api/helpers';
import {CountryCode} from './countries';

// List of subdivisions based on https://github.com/stefangabos/world_countries/blob/master/data/subdivisions/subdivisions.json
// that corresponds to the ISO 3166-2 standard for country subdivisions.

// The first two characters represent the country code
export const SubdivisionList = {
  // Canada
  CA: {
    'CA-AB': 'Alberta',
    'CA-BC': 'British Columbia',
    'CA-MB': 'Manitoba',
    'CA-NB': 'New Brunswick',
    'CA-NL': 'Newfoundland and Labrador',
    'CA-NS': 'Nova Scotia',
    'CA-NT': 'Northwest Territories',
    'CA-NU': 'Nunavut',
    'CA-ON': 'Ontario',
    'CA-PE': 'Prince Edward Island',
    'CA-QC': 'Quebec',
    'CA-SK': 'Saskatchewan',
    'CA-YT': 'Yukon',
  },

  // United States
  US: {
    'US-AL': 'Alabama',
    'US-AK': 'Alaska',
    'US-AZ': 'Arizona',
    'US-AR': 'Arkansas',
    'US-CA': 'California',
    'US-CO': 'Colorado',
    'US-CT': 'Connecticut',
    'US-DE': 'Delaware',
    'US-FL': 'Florida',
    'US-GA': 'Georgia',
    'US-HI': 'Hawaii',
    'US-ID': 'Idaho',
    'US-IL': 'Illinois',
    'US-IN': 'Indiana',
    'US-IA': 'Iowa',
    'US-KS': 'Kansas',
    'US-KY': 'Kentucky',
    'US-LA': 'Louisiana',
    'US-ME': 'Maine',
    'US-MD': 'Maryland',
    'US-MA': 'Massachusetts',
    'US-MI': 'Michigan',
    'US-MN': 'Minnesota',
    'US-MS': 'Mississippi',
    'US-MO': 'Missouri',
    'US-MT': 'Montana',
    'US-NE': 'Nebraska',
    'US-NV': 'Nevada',
    'US-NH': 'New Hampshire',
    'US-NJ': 'New Jersey',
    'US-NM': 'New Mexico',
    'US-NY': 'New York',
    'US-NC': 'North Carolina',
    'US-ND': 'North Dakota',
    'US-OH': 'Ohio',
    'US-OK': 'Oklahoma',
    'US-OR': 'Oregon',
    'US-PA': 'Pennsylvania',
    'US-RI': 'Rhode Island',
    'US-SC': 'South Carolina',
    'US-SD': 'South Dakota',
    'US-TN': 'Tennessee',
    'US-TX': 'Texas',
    'US-UT': 'Utah',
    'US-VT': 'Vermont',
    'US-VA': 'Virginia',
    'US-WA': 'Washington',
    'US-WV': 'West Virginia',
    'US-WI': 'Wisconsin',
    'US-WY': 'Wyoming',
    'US-DC': 'District of Columbia',
    'US-AS': 'American Samoa',
    'US-GU': 'Guam',
    'US-MP': 'Northern Mariana Islands',
    'US-PR': 'Puerto Rico',
    'US-UM': 'United States Minor Outlying Islands',
    'US-VI': 'Virgin Islands, U.S.',
  },
} as const satisfies Record<
  Extract<CountryCode, 'CA' | 'US'>,
  Record<string, string>
>;

// Extract subdivision codes for each country
export type SubdivisionCodesByCountry = {
  [K in keyof typeof SubdivisionList]: keyof (typeof SubdivisionList)[K];
};

// Union type of all subdivision codes across all countries
export type SubdivisionCode =
  SubdivisionCodesByCountry[keyof typeof SubdivisionList];

export const SubdivisionLabels = {
  ...SubdivisionList.CA,
  ...SubdivisionList.US,
} as const satisfies Record<SubdivisionCode, string>;

const subdivisionCodesByCountry = {
  CA: keys(SubdivisionList.CA),
  US: keys(SubdivisionList.US),
} as const satisfies {
  [K in keyof typeof SubdivisionList]: Array<SubdivisionCodesByCountry[K]>;
};

// Schema for a specific country's subdivisions
export function getSubdivisionSchemaForCountry<
  T extends keyof typeof SubdivisionList,
>(countryCode: T) {
  const codes = subdivisionCodesByCountry[countryCode];
  return zodEnum(codes);
}

// All subdivision codes as a flat array
export const SubdivisionCodes = Object.values(SubdivisionList).flatMap(
  (subdivisions) => keys(subdivisions)
) as SubdivisionCode[];

// Schema for all subdivision codes (union)
export const SubdivisionCodeSchema = zodEnum(SubdivisionCodes);
export const isSubdivisionCode = zodTypeguard(SubdivisionCodeSchema);

export function getRegionLabel({
  country,
  region,
}: {
  country?: CountryCode;
  region?: SubdivisionCode | string;
}) {
  if ((country === 'US' || country === 'CA') && isSubdivisionCode(region)) {
    return SubdivisionLabels[region];
  }

  return region;
}
